/**
 * CartActions — parked-carts menu, park, and clear.
 *
 * Lifted out of the cart header so that header could be deleted entirely. It
 * was a full row tall and held a title, a count and these two buttons, leaving
 * most of its width empty — height the item list needed more.
 *
 * The item count moved to the totals ("Subtotal · N items"), where it sits next
 * to the money it explains.
 *
 * Park and clear are always rendered, disabled when the cart is empty, rather
 * than mounted conditionally: appearing and disappearing would shift the whole
 * action row as the first item is scanned.
 */
import { useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, History, PauseCircle, Trash2 } from 'lucide-react';
import { useCartStore } from '@/store/useCartStore';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

export function CartActions() {
  const items = useCartStore((s) => s.items);
  const parked = useCartStore((s) => s.parked);
  const parkCurrent = useCartStore((s) => s.parkCurrent);
  const recallParked = useCartStore((s) => s.recallParked);
  const discardParked = useCartStore((s) => s.discardParked);
  const clearCart = useCartStore((s) => s.clearCart);

  const [recallConfirmId, setRecallConfirmId] = useState<string | null>(null);
  const hasItems = items.length > 0;

  // F4 → park. Moved here with the button it triggers.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'F4') {
        e.preventDefault();
        if (useCartStore.getState().items.length > 0) parkCurrent();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [parkCurrent]);

  const handleRecall = (id: string) => {
    // Recalling replaces the active cart, so confirm when one is in progress.
    if (hasItems) setRecallConfirmId(id);
    else recallParked(id);
  };

  const iconBtn =
    'inline-flex h-11 w-10 shrink-0 items-center justify-center rounded-lg border border-stroke-hairline/15 text-fg-secondary transition-colors hover:bg-bg-subtle hover:text-fg-primary disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-secondary';

  return (
    <>
      {parked.length > 0 && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              title="Parked carts"
              className="inline-flex h-11 shrink-0 items-center gap-1 rounded-lg border border-stroke-hairline/15 px-2.5 text-label text-fg-secondary transition-colors hover:bg-bg-subtle hover:text-fg-primary"
            >
              <History size={15} />
              {parked.length}
              <ChevronDown size={13} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 w-72 rounded-lg border border-stroke-hairline/15 bg-bg-surface p-1 shadow-e2"
            >
              {parked.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-bg-subtle"
                >
                  <button
                    type="button"
                    onClick={() => handleRecall(p.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="truncate text-body text-fg-primary">
                      {p.label} · {p.itemCount} {p.itemCount === 1 ? 'item' : 'items'}
                    </div>
                    <div className="text-label uppercase text-fg-tertiary tabular-nums">
                      {formatCurrency(p.totalAmount)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => discardParked(p.id)}
                    title="Discard parked cart"
                    className="shrink-0 rounded p-1 text-fg-tertiary hover:text-danger-ink"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}

      <button
        type="button"
        onClick={() => parkCurrent()}
        disabled={!hasItems}
        title="Park current cart (F4)"
        className={cn(iconBtn, 'w-auto gap-1.5 px-3 text-label')}
      >
        <PauseCircle size={15} /> Park
      </button>

      <button
        type="button"
        onClick={clearCart}
        disabled={!hasItems}
        title="Clear cart"
        className={cn(iconBtn, 'hover:text-danger-ink')}
      >
        <Trash2 size={15} />
      </button>

      <Dialog open={!!recallConfirmId} onOpenChange={(o) => !o && setRecallConfirmId(null)}>
        <DialogContent title="Replace current cart?" className="w-[420px]">
          <div className="space-y-4 px-5 py-4">
            <p className="text-body text-fg-secondary">
              Recalling a parked cart will replace the active cart. Park the current
              cart first if you want to keep it.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  parkCurrent();
                  if (recallConfirmId) recallParked(recallConfirmId);
                  setRecallConfirmId(null);
                }}
              >
                Park &amp; recall
              </Button>
              <Button
                onClick={() => {
                  if (recallConfirmId) recallParked(recallConfirmId);
                  setRecallConfirmId(null);
                }}
              >
                Discard &amp; recall
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
